import {
    ArgoAttributeSchemaTypeEnum,
    GetSchemaDocument,
} from '../../types/db';
import { accessor } from '../store';
import {
    RouteEntityDetail,
} from '@redviking/argonaut-core-ui/src/components/EntityDetail';
import { Route, type RouteConfig } from 'vue-router';
import RouterViewSlider from '@redviking/argonaut-core-ui/src/components/RouterViewSlider';
import { messages } from 'src/i18n/i18n';
import {
    validateAttributeSelectAcl,
    validateAttributeUpdateAcl,
} from './attributes.validations';
import { entityDetailRoute } from '../components/EntityDetail';
import { Notify } from '../notifications';
import { entityPageBannerTitle } from 'src/util/formatting';
import { gqlClient } from '../util/gql-client';
import { AttributeForSchemaView, AttributeSchemaEntity, createAttribute, saveAttribute } from './attribute.entity';

const attributesRoutes: RouteConfig[] = [
    {
        path: '/attributes',
        name: 'attributesGroup',
        redirect: { name: 'attrSchemaList' },
        component: RouterViewSlider,
        meta: {
            navigationGuard: () => validateAttributeSelectAcl(),
            navbarTitle: messages.attribute.titles.attrs.nav,
            navbarIcon: 'mdi-vector-polyline',
            transition: 'vertical',
            featureFlags: [ 'attributes' ],
        },
        children: [
            {
                path: 'schemas',
                name: 'attrSchemas',
                redirect: (to: Route) => ({ name: 'attrSchemaList', params: to.params }),
                component: RouterViewSlider,
                meta: {
                    navigationGuard: () => validateAttributeSelectAcl(),
                    transition: 'horizontal',
                },
                children: [
                    {
                        name: 'attrSchemaList',
                        path: '',
                        component: () => import(/* webpackChunkName: "attributes" */ './Schemas.view.vue'),
                        meta: {
                            pageBannerSubtitle: messages.attribute.titles.schemas.pageBannerSubtitle,
                            pageBannerTitle: messages.attribute.titles.schemas.pageBannerTitle,
                        },
                    },
                    entityDetailRoute<'attributeschema'>({
                        path: ':schemaId',
                        name: 'attribute-schema-maintenance',
                        component: RouteEntityDetail,
                        redirect: to => ({ name: 'attrSchemaSettings', params: to.params }),
                        meta: {
                            returnRoute: (route: Route) => ({ name: 'attrSchemaList', query: route.query }),
                            canEdit: validateAttributeUpdateAcl,
                            entityType: 'attributeschema',
                            async getEntity (to) {
                                if (to.query.mode === 'create') {
                                    const ret: AttributeSchemaEntity = {
                                        entitiesToUpdateOnSave: [],
                                        schema: {
                                            meta: {},
                                            enabled: true,
                                            attributes: [],
                                            definitions: [],
                                            description: '',
                                            color: '#2f4050',
                                            id: to.params.schemaId,
                                            name: 'New Attribute Schema',
                                            type: ArgoAttributeSchemaTypeEnum.Material,
                                        },
                                    };
                                    return {
                                        entity: ret,
                                    };
                                }
                                const { schema } = await gqlClient.request({
                                    document: GetSchemaDocument,
                                    variables: {
                                        schemaId: to.params.schemaId,
                                    },
                                });
                                if (schema) {
                                    const ret: AttributeSchemaEntity = {
                                        entitiesToUpdateOnSave: [],
                                        schema: {
                                            id: schema.id,
                                            name: schema.name,
                                            type: schema.type,
                                            meta: schema.meta,
                                            color: schema.color,
                                            enabled: schema.enabled,
                                            description: schema.description,
                                            definitions: schema.definitions.map(def => ({
                                                ...def,
                                                new: false,
                                            })),
                                            attributes: schema.attributes.map(attr => {
                                                const attrRet: AttributeForSchemaView = {
                                                    new: false,
                                                    id: attr.id,
                                                    name: attr.name,
                                                    meta: attr.meta,
                                                    enabled: attr.enabled,
                                                    created_at: attr.created_at,
                                                    description: attr.description,
                                                    attribute_data: attr.attribute_data,
                                                    root_attribute_id: attr.root_attribute_id,
                                                    location_attributes_aggregate: attr.location_attributes_aggregate,
                                                    material_attributes_aggregate: attr.material_attributes_aggregate,
                                                    material_class_attributes_aggregate: attr.material_class_attributes_aggregate,
                                                    material_model_attributes_aggregate: attr.material_model_attributes_aggregate,
                                                };
                                                return attrRet;
                                            }),
                                        },
                                    };
                                    return {
                                        entity: ret,
                                    };
                                } else {
                                    const err = new Error('Unknown Attribute Schema');
                                    Notify.error(err);
                                    throw err;
                                }
                            },
                            createEntity: createAttribute,
                            saveEntity: saveAttribute,
                            pageBannerTitle: () => entityPageBannerTitle('attributeschema', accessor.entityAsType('attributeschema')?.schema.name),
                        },
                        children: [
                            {
                                path: 'settings',
                                name: 'attrSchemaSettings',
                                component: () => import(/* webpackChunkName: "attributes" */ './SchemaMaintenance.view.vue'), // a component for the settings page content
                                meta: {
                                    tab: { // the presence of this prop indicates this is a tab
                                        label: 'Settings',
                                        icon: 'mdi-cog',
                                    },
                                    navigationGuard: () => true,
                                },
                            },
                            {
                                path: 'attributes',
                                name: 'attrSchemaAttributes',
                                component: () => import(/* webpackChunkName: "attributes" */ './Schema-Attributes.view.vue'),
                                meta: {
                                    tab: {
                                        label: 'Attributes',
                                        icon: 'mdi-vector-polyline',
                                    },
                                    pageBannerSubtitle: 'Schema Attributes',
                                    navigationGuard: () => validateAttributeSelectAcl(),
                                },
                            },
                        ],
                    }),
                ],
            },
        ],
    },
];

export default attributesRoutes;
